// ============================================================
// The owner shell's one tab control.
//
// There were three. This module's own segmented pills (unit sub-tabs), a row
// of outlined boxes on the Money tab, and a third bar for the property status
// filter — each invented where it was needed, each a slightly different height,
// radius and active state. On a product whose brand guide allows three colours
// total, three tab styles on adjacent screens is the thing that reads as
// unfinished, well before any individual screen does.
//
// One control, three shapes of content:
//   • icon + label      unit sub-tabs (Overview / Inspection / …)
//   • label only        Money (Rent / Costs / Plans)
//   • label + count     property status filter (Active (10) / Rented (5) / …)
// ============================================================
import React from 'react';
import { useLanguage } from '../../components/LanguageContext';
import { M, T } from './pmTokens';

export interface SegItem {
  key: string;
  /** A translation key — resolved here, so callers pass keys not strings. */
  label: string;
  icon?: React.ComponentType<any>;
  /** Shown in brackets after the label. 0 is rendered, `undefined` is not. */
  count?: number;
}

const Segmented: React.FC<{
  tabs: SegItem[];
  value: string;
  onChange: (k: string) => void;
  /** Rendered under the track — the refresh button on the PM dashboard. */
  right?: React.ReactNode;
  /** Drops the outer padding/rule when the control sits inside a card. */
  bare?: boolean;
}> = ({ tabs, value, onChange, right, bare }) => {
  const { t } = useLanguage();
  // TWO ROWS, not one squeezed line.
  //
  // Four or five tabs across a phone left ~80px a pill. The control answered
  // that by shrinking the type to 12px, dropping the icons and clipping the
  // words — "Maintenance" became "Mainten…", "Documents" became "Docume…" —
  // which is the one thing the labels must never do, since the word is what
  // people navigate by. A clipped word also reads as a bug rather than as a
  // design. Height is the cheap dimension on a phone, so the track spends it:
  // a grid of ceil(n/2) columns turns any bar of four or more into two even
  // rows, and the icon, the full label and the 13px type all come back.
  const twoRows = tabs.length >= 4;
  const cols = twoRows ? Math.ceil(tabs.length / 2) : tabs.length;
  const fs = 13;
  const iconSize = 19;

  return (
    <div
      className="pm-seg-wrap"
      style={
        bare
          ? { marginBottom: 16 }
          : { padding: '12px 16px 14px', background: '#fff', borderBottom: `1px solid ${M.hair}` }
      }
    >
      <div
        className="pm-seg"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gap: 4, background: M.track, borderRadius: 14, padding: 4,
        }}
      >
        {tabs.map(({ key, label, icon: Icon, count }) => {
          const active = key === value;
          return (
            <button
              key={key}
              onClick={() => onChange(key)}
              className="pt-tap"
              aria-pressed={active}
              style={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '9px 4px',
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                background: active ? '#fff' : 'transparent',
                boxShadow: active ? '0 1px 4px rgba(0,0,0,0.18)' : 'none',
                color: active ? M.black : M.ink2,
              }}
            >
              {Icon && iconSize > 0 && <Icon size={iconSize} color={active ? M.goldInk : M.ink3} />}
              <span
                style={{
                  fontSize: fs,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  textAlign: 'center',
                  overflow: 'hidden',
                  maxWidth: '100%',
                  letterSpacing: 0,
                }}
              >
                {t(label)}
                {count !== undefined && (
                  // The count is secondary to the label: same line, lighter,
                  // so scanning the tabs reads as words rather than as sums.
                  <span style={{ fontWeight: 700, color: active ? M.ink3 : M.ink4 }}> {count}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {right}
    </div>
  );
};

export default Segmented;

/** The type the owner module's tab tables are declared with. */
export type Tab = SegItem;
